/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {UntypedFormControl, Validators} from '@angular/forms';

import {BehaviorSubject} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {EmpZoneDependencyUtils} from '../emp-zone-dependency.utils';
import {
  EmpZoneDependency,
  EmpZoneDependencyAction,
  Field,
  FieldEventTrace,
  GroupZoneDescriptor,
  Value,
  ZoneDescriptor
} from '../form-generator.interfaces';
import {FormGeneratorUtils} from '../form-generator.utils';

export abstract class AbstractField extends Destroyable implements Field {

  /**
   * Field definition associated to this field instance.
   */
  public descriptor: ZoneDescriptor = null;

  /**
   * Angular2 form control this field instance is bound to.
   */
  public control: UntypedFormControl = null;

  /**
   * Value object associated with this field instance.
   */
  public value: Value = null;

  /**
   * Whether this field is read-only or not
   */
  public readOnly: boolean = false;

  /**
   * Locale associated with the generated form
   */
  public locale: string = 'en-US';

  /**
   * Currency associated with the generated from
   */
  public currency: string = 'EUR';

  public focus: any;

  public groupZoneDescriptor: GroupZoneDescriptor;

  public fieldFocused$: BehaviorSubject<FieldEventTrace>;
  public fieldBlurred$: BehaviorSubject<FieldEventTrace>;
  public dependencyTriggered$: BehaviorSubject<EmpZoneDependency>;

  public onFocus() {
    this.descriptor.isFocused = true;
    this.groupZoneDescriptor.isFocused = true;
    this.fieldFocused$.next({
      value: this.value.value,
      descriptor: this.descriptor
    });
  }

  public onBlur() {
    this.descriptor.isFocused = false;
    this.groupZoneDescriptor.isFocused = false;
    this.fieldBlurred$.next({
      value: this.value.value,
      descriptor: this.descriptor
    });
  }

  protected bindDependencyTriggered() {
    if (this.groupZoneDescriptor
      ?.dependencies
      ?.map(dependency => dependency.target)
      ?.find(target => target === this.descriptor.zoneID
        || FormGeneratorUtils.simplifyZoneName(target) === this.descriptor.zoneID)) {
      this.dependencyTriggered$
        .pipe(
          tap((dependency) => {
            switch (dependency?.action) {
              case EmpZoneDependencyAction.FILTER:
                this.handleFilter(dependency);
                break;
              case EmpZoneDependencyAction.SET_DISABLED:
                this.handleSetDisabled(dependency);
                break;
              case EmpZoneDependencyAction.SET_REQUIRED:
                this.handleSetRequired(dependency);
                break;
              case EmpZoneDependencyAction.SET_CALCULATION_DISABLED:
                this.handleSetCalculationDisabled(dependency);
                break;
            }
            this.afterDependencyHandled(dependency);
          }),
          takeUntil(this.componentDestroyed$)
        )
        .subscribe();
    }
  }

  protected handleFilter(dependency: EmpZoneDependency) {
    // to be implemented
  }

  protected afterDependencyHandled(dependency: EmpZoneDependency) {
    // to be implemented
  }

  protected handleSetDisabled(dependency: EmpZoneDependency) {
    if (EmpZoneDependencyUtils.isReset(dependency)) {
      this.readOnly = false;
    } else {
      this.readOnly = dependency.actionValue === 'true';
      this.value.value = '';
      this.value.transId &&= null;
    }
  }

  protected handleSetRequired(dependency: EmpZoneDependency) {
    if (EmpZoneDependencyUtils.isReset(dependency)) {
      this.descriptor.isRequired = this.descriptor._isRequired;
    } else {
      this.descriptor.isRequired = dependency.actionValue === 'true';
    }
    this.descriptor.isRequired ? this.control.addValidators(Validators.required) : this.control.removeValidators(Validators.required);
    this.control.updateValueAndValidity();
  }

  private handleSetCalculationDisabled(dependency: EmpZoneDependency) {
    if (!EmpZoneDependencyUtils.isReset(dependency)) {
      this.readOnly = false;
    } else {
      if (this.descriptor.calculation?.calculationType === 'FORCE_CALCULATION') {
        this.readOnly = true;
      }
    }
  }
}
